/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { CustomFieldService, ICustomFieldSearchFilters } from 'app/core/services/custom-field-service';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import { apiCall } from 'app/shared/lib/api/api';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class EditMemberUserDefinedFields extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected customValues: Core.CustomValues | null = null;
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected displayCustomLookups: boolean = false;
    protected displayCustomText: boolean = false;

    protected lookupsArray: Array<Core.CustomFieldChoice>[] = [];
    protected searchFilters: ICustomFieldSearchFilters = { targetCode: 'CD.Member' };
    protected memberUDFCount: number = 0;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly customFieldContext: CustomFieldContext,
        private readonly customFieldService: CustomFieldService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async bind(): Promise<void> {
        await this.loadMemberDefinitions();
        // bind() is an async here to ensure following two functions are called only after memberUDFCount is loaded
        this.loadCustomFieldTargets();
        this.loadLookups();
    }

    ////****************** Functions ************************************************////

    // Get the Labels for Member Text and Lookup fields
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Member', { definitionCount: this.memberUDFCount, refreshCache: true });

        const customLookups: Core.CustomFieldChoice[] = this.customFieldTargetColumns.lookup?.filter(lookup => lookup.isActive) || [];
        const customTexts: Core.CustomFieldChoice[] = this.customFieldTargetColumns.text?.filter(text => text.isActive) || [];

        this.displayCustomLookups = customLookups.length > 0;
        this.displayCustomText = customTexts.length > 0;

    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadMemberDefinitions() {
        const options: StandardLoadOptions = {
            all: true,
            includeInactive: true
        };
        try {
            this.state.beginLoad();
            const response = await this.customFieldService.loadCustomFields(this.searchFilters, options);
            this.memberUDFCount = response.results.length / 2; // 2 because each UDF has 2 definitions, one for text and one for lookup
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    // Get the DDL values for Member Lookup fields
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const lookupIncludes = [
                'definition.target'
            ];
            const lookupPromises = [];
            for(let i = 1; i <= this.memberUDFCount; i++) {
                const lookupPromise = this.customFieldService.loadCustomFieldChoicesByDefinition('CD.Member', `lookup.choice${i}`, {}, lookupIncludes);
                lookupPromises.push(lookupPromise);
            }

            const results = [];
            const response = await Promise.all(lookupPromises);

            for(let i = 0; i < response.length; i++) {
                results.push(response[i].results);
            }

            this.lookupsArray = results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
